import { Avatar } from "../types";

export type RuntimePackKey = "patient_runtime" | "dentist_runtime";

type VisemeKey =
  | "closed"
  | "a"
  | "i"
  | "u"
  | "e"
  | "o"
  | "smile"
  | "n"
  | "quarter_a"
  | "quarter_i"
  | "quarter_u"
  | "quarter_o"
  | "half_a"
  | "half_i"
  | "half_u"
  | "half_o";

export type CanonicalPoseImageUrls = Record<string, string>;

export interface EyePosition {
  top: string;
  left: string;
  width: string;
  height: string;
}

export interface InterviewAvatarAssets {
  runtimePackRoot: string;
  baseImageUrl: string;
  /** 口ありの元画像（avatar-idle jawレイヤー用） */
  mouthImageUrl: string;
  eyesHalfBlinkUrl: string;
  eyesBlinkUrl: string;
  jawMaskUrl: string;
  browMaskUrl: string;
  visemeImageUrls: Record<VisemeKey, string>;
  canonicalManifestUrl: string;
  eyePosition?: EyePosition;
}

interface CanonicalManifestEntry {
  image?: string;
  selectedImage?: string;
}

interface CanonicalManifest {
  poses?: Record<string, Record<string, CanonicalManifestEntry>>;
}

const assetCache = new Map<string, InterviewAvatarAssets>();
const KNOWN_DENTAL_POSES = [
  "REST",
  "AA",
  "E",
  "I",
  "U",
  "O",
  "PBM",
  "SZ",
  "SH_CH",
  "TDN",
  "KG",
  "FV",
  "L_R",
  "DENTAL_OPEN",
];

function buildVisemeImageUrls(root: string): Record<VisemeKey, string> {
  return {
    closed: `${root}/viseme_closed.jpg`,
    a: `${root}/viseme_a.jpg`,
    i: `${root}/viseme_i.jpg`,
    u: `${root}/viseme_u.jpg`,
    e: `${root}/viseme_e.jpg`,
    o: `${root}/viseme_o.jpg`,
    smile: `${root}/viseme_smile.jpg`,
    n: `${root}/viseme_n.jpg`,
    quarter_a: `${root}/viseme_quarter_a.jpg`,
    quarter_i: `${root}/viseme_quarter_i.jpg`,
    quarter_u: `${root}/viseme_quarter_u.jpg`,
    quarter_o: `${root}/viseme_quarter_o.jpg`,
    half_a: `${root}/viseme_half_a.jpg`,
    half_i: `${root}/viseme_half_i.jpg`,
    half_u: `${root}/viseme_half_u.jpg`,
    half_o: `${root}/viseme_half_o.jpg`,
  };
}

function resolveRuntimeRoot(avatar?: Avatar): { root: string; cacheKey: string } {
  // profile別runtimePath優先
  const runtimePath = avatar?.videoFiles?.runtimePath;
  if (runtimePath) {
    const root = `/videos/assets/${runtimePath}`;
    return { root, cacheKey: runtimePath };
  }
  // fallback: shared
  const runtimePackKey = avatar?.videoFiles?.runtimePackKey;
  if (runtimePackKey === "patient_runtime" || runtimePackKey === "dentist_runtime") {
    return { root: `/videos/assets/shared/${runtimePackKey}`, cacheKey: runtimePackKey };
  }
  const fallback = avatar?.type === "dentist" ? "dentist_runtime" : "patient_runtime";
  return { root: `/videos/assets/shared/${fallback}`, cacheKey: fallback };
}

export function resolveInterviewAvatarAssets(avatar?: Avatar): InterviewAvatarAssets {
  const { root, cacheKey } = resolveRuntimeRoot(avatar);
  const cached = assetCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const isDentist = avatar?.type === "dentist";
  const runtimeClosedImageUrl = `${root}/viseme_closed.jpg`;
  const mouthImageUrl = runtimeClosedImageUrl;
  const assets = {
    runtimePackRoot: root,
    baseImageUrl: runtimeClosedImageUrl,
    mouthImageUrl,
    eyesHalfBlinkUrl: `${root}/eyes_half_blink.png`,
    eyesBlinkUrl: `${root}/eyes_blink.png`,
    jawMaskUrl: `${root}/${isDentist ? "jaw_mask_dentist.png" : "jaw_mask_patient.png"}`,
    browMaskUrl: `${root}/${isDentist ? "brow_mask_dentist.png" : "brow_mask_patient.png"}`,
    visemeImageUrls: buildVisemeImageUrls(root),
    canonicalManifestUrl: `${root}/canonical/manifest.json`,
  };
  assetCache.set(cacheKey, assets);
  return assets;
}

const canonicalAssetCache = new Map<string, Promise<CanonicalPoseImageUrls | undefined>>();

function isCanonicalAssetSelfCheckEnabled(): boolean {
  if (typeof window === "undefined") return false;

  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("visemeCanonicalSelfCheck") === "1" ||
      window.localStorage.getItem("visemeCanonicalSelfCheck") === "1"
    );
  } catch {
    return false;
  }
}

async function canonicalImageExists(url: string): Promise<boolean> {
  try {
    const res = await fetch(url, { method: "HEAD" });
    return res.ok;
  } catch {
    return false;
  }
}

export async function fetchCanonicalPoseImageUrls(avatar?: Avatar): Promise<CanonicalPoseImageUrls | undefined> {
  const { root, cacheKey } = resolveRuntimeRoot(avatar);
  const profileKey = avatar?.videoFiles?.assetProfileKey;
  const canonicalCacheKey = `${cacheKey}:canonical:${profileKey ?? "default"}`;
  const cached = canonicalAssetCache.get(canonicalCacheKey);
  if (cached) {
    return cached;
  }

  const promise = (async () => {
    try {
      const res = await fetch(`${root}/canonical/manifest.json`);
      if (!res.ok) return undefined;

      const manifest = (await res.json()) as CanonicalManifest;
      const posesByProfile = manifest.poses ?? {};
      const profileManifest =
        (profileKey && posesByProfile[profileKey])
        || posesByProfile[Object.keys(posesByProfile)[0]];
      if (!profileManifest) return undefined;

      const urls: CanonicalPoseImageUrls = {};
      for (const [pose, entry] of Object.entries(profileManifest)) {
        const imageName = entry.image ?? entry.selectedImage;
        if (imageName) {
          urls[pose] = `${root}/canonical/${imageName}`;
        }
      }

      if (!isCanonicalAssetSelfCheckEnabled()) {
        return Object.keys(urls).length > 0 ? urls : undefined;
      }

      const verifiedEntries = await Promise.all(
        Object.entries(urls).map(async ([pose, url]) => [pose, url, await canonicalImageExists(url)] as const)
      );
      const verifiedUrls: CanonicalPoseImageUrls = {};
      const missingImages: string[] = [];
      for (const [pose, url, exists] of verifiedEntries) {
        if (exists) {
          verifiedUrls[pose] = url;
        } else {
          missingImages.push(pose);
        }
      }

      const loadedPoses = Object.keys(verifiedUrls).sort();
      const fallbackPoses = KNOWN_DENTAL_POSES.filter((pose) => !verifiedUrls[pose]);
      console.info("[avatar-assets] canonical runtime self-check", {
        profile: profileKey,
        runtimePath: root.replace("/videos/assets/", ""),
        loadedPoses,
        fallbackPoses,
        missingImages,
      });

      return loadedPoses.length > 0 ? verifiedUrls : undefined;
    } catch {
      return undefined;
    }
  })();

  canonicalAssetCache.set(canonicalCacheKey, promise);
  return promise;
}

const metaCache = new Map<string, EyePosition | null>();

export async function fetchEyePosition(avatar?: Avatar): Promise<EyePosition | undefined> {
  const { root, cacheKey } = resolveRuntimeRoot(avatar);
  if (metaCache.has(cacheKey)) {
    return metaCache.get(cacheKey) ?? undefined;
  }
  try {
    const res = await fetch(`${root}/meta.json`);
    if (!res.ok) {
      metaCache.set(cacheKey, null);
      return undefined;
    }
    const meta = await res.json();
    const ep = meta.eyePosition ?? null;
    metaCache.set(cacheKey, ep);
    return ep ?? undefined;
  } catch {
    metaCache.set(cacheKey, null);
    return undefined;
  }
}
